import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAccessData } from 'features/accessControl/hooks/useAccessData'
import { countActiveThisWeek, peopleSummary, visibleAccessRows } from 'features/accessControl/visibleUsers'
import { treeAccessSentence } from 'features/accessControl/treeCounts'
import PeopleTable from 'features/accessControl/components/PeopleTable'
import AccessTree from 'features/accessControl/components/AccessTree'
import AccessAddModal from 'features/accessControl/components/AccessAddModal'
import InvitationsList from 'features/accessControl/components/InvitationsList'
import RequestsList from 'features/accessControl/components/RequestsList'
import RevokedList from 'features/accessControl/components/RevokedList'
import RoleCoverageView from 'features/accessControl/components/RoleCoverageView'
import ManageFilesPermissions from 'features/bpDetails/Components/files/ManageFilesPermissions'
import { SkeletonRows } from 'components/Skeleton'
import SearchableSelect from 'components/SearchableSelect'
import { ROLE_OPTIONS } from 'utils/roleOptions'
import { updateBpUser, revokeAccessToBp } from 'services/BpService'
import { ink, line, radius, surface, font, transition } from 'features/dashboard/tokens'
import { RAG } from 'features/dashboard/charts/scoreColour'

const AccessManager = ({ scopeType, scopeId, scopeName, canManage, isOwner = false, bp, onUsersChanged }) => {
  const { users, invitations, requests, revoked, loading, usersLoaded, refetch, api } = useAccessData(scopeType, scopeId)
  const currentUserId = useSelector(state => state.auth?.user?.id ?? null)
  const visibleUsers = useMemo(() => visibleAccessRows(users, currentUserId), [users, currentUserId])
  const activeThisWeek = useMemo(() => countActiveThisWeek(visibleUsers), [visibleUsers])
  useEffect(() => {
    if (!loading && usersLoaded && onUsersChanged) onUsersChanged(users)
  }, [users, loading, usersLoaded, onUsersChanged])
  const [tab, setTab] = useState('people')
  const [treeSummary, setTreeSummary] = useState(null)
  const treeSentence = treeAccessSentence(treeSummary, scopeType)
  const [adding, setAdding] = useState(false)
  const [filesTarget, setFilesTarget] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const navigate = useNavigate()
  const isContainer = scopeType !== 'building'
  const canManageRequests = isContainer ? canManage : !!bp?.isOwner

  useEffect(() => { setSelectedIds(new Set()) }, [scopeType, scopeId])
  useEffect(() => { if (tab !== 'people') setSelectedIds(new Set()) }, [tab])
  useEffect(() => { setSelectedIds(new Set()) }, [users])

  const changeRole = async (row, role) => {
    try {
      if (row.isDirectMember || row.source === 'direct') {
        await api.updateUser(row.id, { relationType: role })
      } else {
        await Promise.all((row.buildings || []).map((b) => updateBpUser({ bpId: b.bpId, relationId: b.rowId, body: { relationType: role } })))
      }
      toast.success('Role updated.')
      refetch()
    } catch {
      toast.error('Could not update role.')
    }
  }
  const revoke = async (row) => {
    try {
      if (row.isDirectMember || row.source === 'direct') {
        await api.revokeUser(row.id)
      } else {
        await Promise.all((row.buildings || []).map((b) => revokeAccessToBp({ bpId: b.bpId, relationId: b.rowId })))
      }
      toast.success('Access removed.')
      refetch()
    } catch {
      toast.error('Could not remove access.')
    }
  }
  const editBuildingRole = async (b, role) => {
    try {
      await Promise.all((b.rowIds || [b.rowId]).map((rid) => updateBpUser({ bpId: b.bpId, relationId: rid, body: { relationType: role } })))
      toast.success('Role updated for ' + b.bpName + '.')
      refetch()
    } catch {
      toast.error('Could not update that building.')
    }
  }
  const removeBuilding = async (b) => {
    try {
      await Promise.all((b.rowIds || [b.rowId]).map((rid) => revokeAccessToBp({ bpId: b.bpId, relationId: rid })))
      toast.success('Removed from ' + b.bpName + '.')
      refetch()
    } catch {
      toast.error('Could not remove from that building.')
    }
  }
  const jumpToSource = (row) => {
    if (row.source === 'building' && row.sourceId) navigate(`/bp-list/${row.sourceId}/accessControl`)
    else if (row.source === 'estate' && row.sourceId) navigate(`/estates/${row.sourceId}?tab=access`)
    else if (row.source === 'portfolio' && row.sourceId) navigate(`/portfolios/${row.sourceId}?tab=access`)
  }
  const toggleRow = (id) => setSelectedIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  const toggleAll = (ids) => setSelectedIds((prev) => (ids.every((i) => prev.has(i)) ? new Set() : new Set(ids)))
  const bulkChangeRole = async (role) => {
    if (!role) return
    try {
      await Promise.all([...selectedIds].map((id) => api.updateUser(id, { relationType: role })))
      toast.success('Role updated for selected people.')
      refetch()
    } catch {
      toast.error('Could not update role for all selected people.')
    }
  }
  const bulkRevoke = async () => {
    if (!window.confirm('Remove access for the selected people?')) return
    try {
      await Promise.all([...selectedIds].map((id) => api.revokeUser(id)))
      toast.success('Access removed for selected people.')
      refetch()
    } catch {
      toast.error('Could not remove access for all selected people.')
    }
  }
  const tabs = useMemo(() => [
    { key: 'people', label: 'People', count: visibleUsers.length },
    ...(api.supportsRequests && canManageRequests ? [{ key: 'requests', label: 'Requests', count: requests.length }] : []),
    { key: 'invitations', label: 'Invitations', count: invitations.length },
    ...(revoked.length ? [{ key: 'revoked', label: 'Revoked', count: revoked.length }] : []),
    ...(isContainer ? [{ key: 'coverage', label: 'Role coverage' }] : []),
  ], [visibleUsers.length, requests.length, invitations.length, revoked.length, api.supportsRequests, canManageRequests, isContainer])

  return (
    <Wrap>
      <Header>
        <div>
          <H1>Access</H1>
          <Sub>Who can view and manage {scopeName}</Sub>
          {!loading && usersLoaded && (
            <>
              <PeopleSummary data-testid='access-people-summary'>{peopleSummary(visibleUsers.length, activeThisWeek)}</PeopleSummary>
              {isContainer && treeSentence && (
                <PeopleBreakdown data-testid='access-people-breakdown'>{treeSentence} People who get access through a domain rule are shown in the Domain rules section.</PeopleBreakdown>
              )}
            </>
          )}
        </div>
        {canManage && !isContainer && <AddBtn data-tour-id='accessControl.add-person' onClick={() => setAdding(true)}><PersonAddIcon fontSize='small' /> Add person</AddBtn>}
      </Header>
      <Tabs>{tabs.map((t) => <Pill key={t.key} $on={tab === t.key} onClick={() => setTab(t.key)}>{t.label}{t.count != null && <Count>{t.count}</Count>}</Pill>)}</Tabs>

      {loading ? <SkeletonRows rows={5} gap={10} widths={['100%','100%','100%','100%','100%']} /> : (
        <>
          {tab === 'people' && (
            isContainer ? (
              <AccessTree scopeType={scopeType} scopeId={scopeId} canManage={canManage} isOwner={isOwner} onChanged={refetch} onSummary={setTreeSummary} />
            ) : (
              <>
                {selectedIds.size > 0 && (
                  <BulkBar>
                    <BulkCount>{selectedIds.size} selected</BulkCount>
                    <BulkRoleField>
                      <SearchableSelect ariaLabel='Change role to…' placeholder='Change role to…' options={ROLE_OPTIONS.map(({ value, label }) => ({ value, label }))} onChange={bulkChangeRole} />
                    </BulkRoleField>
                    <BulkDangerBtn onClick={bulkRevoke}>Remove selected</BulkDangerBtn>
                    <BulkClearBtn onClick={() => setSelectedIds(new Set())}>Clear</BulkClearBtn>
                  </BulkBar>
                )}
                <div data-tour-id='accessControl.people'>
                  <PeopleTable rows={users} currentUserId={currentUserId} canManage={canManage} isOwner={isOwner} supportsPerFile={api.supportsPerFile} selectedIds={selectedIds} onToggleRow={toggleRow} onToggleAll={toggleAll} onChangeRole={changeRole} onRevoke={revoke} onEditFiles={(row) => setFilesTarget(row)} onJumpToSource={jumpToSource} onEditBuildingRole={editBuildingRole} onRemoveBuilding={removeBuilding} onOpenBuilding={(b) => navigate(`/bp-list/${b.bpId}`)} />
                </div>
              </>
            )
          )}
          {tab === 'requests' && <RequestsList items={requests} canAccept={canManageRequests} onAccept={(r) => api.acceptRequest(r.id).then(refetch).catch(() => toast.error('Could not accept the request.'))} onDeny={(r, reason) => api.denyRequest(r.id, reason).then(refetch).catch(() => toast.error('Could not deny the request.'))} />}
          {tab === 'invitations' && <InvitationsList items={invitations} canManage={canManage} scopeType={scopeType} scopeId={scopeId} onCancel={(i) => api.deleteInvitation(i.id).then(refetch).catch(() => toast.error('Could not cancel the invitation.'))} onChanged={refetch} />}
          {tab === 'revoked' && <RevokedList items={revoked} />}
          {tab === 'coverage' && <RoleCoverageView scopeType={scopeType} scopeId={scopeId} onChanged={refetch} />}
        </>
      )}

      {adding && <AccessAddModal scopeType={scopeType} scopeId={scopeId} api={api} scopeName={scopeName} onClose={() => setAdding(false)} onDone={refetch} />}
      {filesTarget && bp && <ManageFilesPermissions user={{ id: filesTarget.user.id, relationType: filesTarget.role }} bp={bp} autoOpen showMenuItem={false} onCloseMenu={() => { setFilesTarget(null); refetch() }} />}
    </Wrap>
  )
}

export default AccessManager

const CASCADE_FG = '#0f766e'
const CASCADE_BG = '#e6fbfd'
const CASCADE_BORDER = '#cdeff2'

const Wrap = styled.div`padding:4px 2px;`
const Header = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:12px;`
const H1 = styled.h2`margin:0;font-size:${font.title};color:${ink.base};`
const Sub = styled.div`font-size:${font.body};color:${ink.mute};margin-top:2px;`
const AddBtn = styled.button`display:inline-flex;align-items:center;gap:6px;background:${({ theme }) => theme.colors.secondary};color:#fff;border:none;border-radius:${radius.sm};padding:9px 14px;font-weight:700;font-size:13px;cursor:pointer;`
const Banner = styled.div`background:${CASCADE_BG};border:1px solid ${CASCADE_BORDER};color:${CASCADE_FG};border-radius:${radius.sm};padding:9px 12px;font-size:12.5px;margin-bottom:12px;`
const Tabs = styled.div`display:flex;gap:4px;border-bottom:1px solid ${line.base};margin-bottom:14px;flex-wrap:wrap;`
const Pill = styled.button`display:inline-flex;align-items:center;gap:6px;padding:10px 14px;border:none;background:transparent;color:${({ $on }) => ($on ? ink.base : ink.soft)};font-size:${font.small};font-weight:${({ $on }) => ($on ? 700 : 500)};white-space:nowrap;border-bottom:2px solid ${({ $on, theme }) => ($on ? theme.colors.secondary : 'transparent')};margin-bottom:-1px;cursor:pointer;transition:${transition};&:hover{color:${ink.base};}`
const Count = styled.span`background:${line.base};border-radius:10px;padding:0 6px;font-size:11px;margin-left:6px;`
const PeopleBreakdown = styled.div`font-size:14px;color:#475569;margin-top:2px;line-height:1.45;`
const PeopleSummary = styled.div`font-size:${font.body};color:${ink.base};font-weight:600;margin-top:6px;`
const PeopleHelper = styled.div`font-size:${font.small};color:${ink.mute};margin-bottom:12px;`
const BulkBar = styled.div`display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:${surface.wash};border:1px solid ${line.base};border-radius:${radius.sm};padding:10px 14px;margin-bottom:12px;`
const BulkCount = styled.div`font-size:${font.body};font-weight:700;color:${ink.base};`
const BulkRoleField = styled.div`width:220px;`
const BulkDangerBtn = styled.button`display:inline-flex;align-items:center;padding:9px 14px;border:1px solid ${line.strong};border-radius:${radius.sm};background:${surface.card};color:${RAG.critical.fg};font-size:13px;font-weight:700;cursor:pointer;transition:${transition};&:hover{border-color:${RAG.critical.fg};background:${RAG.critical.bg};}`
const BulkClearBtn = styled.button`background:none;border:none;color:${ink.mute};font-size:13px;font-weight:600;cursor:pointer;padding:6px 8px;transition:${transition};&:hover{color:${ink.base};}`
